import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrafficLight } from '@/fontawesome'
import { StocksStatusLightColor } from '@/models'
import type { StocksStatusLight } from '@/models'
import { formatDate } from '@/utils/helpers'

interface TrafficLightDetailProps {
  stockStatusLight: StocksStatusLight | null
}

type TrafficLightStatus = 'red' | 'yellow' | 'green'

const colorToStatus = (color: StocksStatusLightColor): TrafficLightStatus => {
  switch (color) {
    case StocksStatusLightColor.RED:
      return 'red'
    case StocksStatusLightColor.YELLOW:
      return 'yellow'
    case StocksStatusLightColor.GREEN:
      return 'green'
  }
}

const getStatusLabel = (status: TrafficLightStatus): string => {
  switch (status) {
    case 'red':
      return 'Vorsicht'
    case 'yellow':
      return 'Neutral'
    case 'green':
      return 'Alles im grünen Bereich'
  }
}

const getStatusDescription = (status: TrafficLightStatus): string => {
  switch (status) {
    case 'red':
      return 'Der Markt zeigt Anzeichen erhöhter Volatilität oder negativer Trends. Erhöhte Vorsicht bei neuen Positionen ist angebracht. Bestehende Stopps sollten überprüft werden.'
    case 'yellow':
      return 'Der Markt bewegt sich in einem neutralen Bereich ohne klare Richtung. Sowohl Chancen als auch Risiken sind ausgeglichen. Selektives Vorgehen empfohlen.'
    case 'green':
      return 'Die Marktbedingungen sind günstig. Positive Trends und stabiles Marktumfeld ermöglichen Positionsaufbau. Dennoch sollte Risikomanagement beachtet werden.'
  }
}

export default function TrafficLightDetail({ stockStatusLight }: TrafficLightDetailProps) {
  const status = stockStatusLight !== null ? colorToStatus(stockStatusLight.color) : 'yellow'
  const statusLabel = getStatusLabel(status)
  const statusDescription = getStatusDescription(status)
  const adminComment = stockStatusLight?.comment || ''

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 sm:p-8 shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/50 rounded-lg flex items-center justify-center">
          <FontAwesomeIcon icon={faTrafficLight} className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Börsen-Ampel
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Aktuelle Markteinschätzung
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Traffic Light Display */}
        <div className="flex flex-col items-center justify-center">
          <div className="bg-gray-900 dark:bg-gray-950 rounded-3xl p-8 shadow-xl">
            <div className="flex flex-col gap-6">
              {/* Red Light */}
              <div
                className={`w-24 h-24 rounded-full border-8 transition-all ${
                  status === 'red'
                    ? 'bg-red-500 border-red-600 shadow-2xl shadow-red-500/50 dark:bg-red-400 dark:border-red-500 animate-pulse'
                    : 'bg-red-200 border-red-300 opacity-20 dark:bg-red-900/30 dark:border-red-800'
                }`}
              />

              {/* Yellow Light */}
              <div
                className={`w-24 h-24 rounded-full border-8 transition-all ${
                  status === 'yellow'
                    ? 'bg-yellow-400 border-yellow-500 shadow-2xl shadow-yellow-400/50 dark:bg-yellow-300 dark:border-yellow-400 animate-pulse'
                    : 'bg-yellow-100 border-yellow-200 opacity-20 dark:bg-yellow-900/30 dark:border-yellow-800'
                }`}
              />

              {/* Green Light */}
              <div
                className={`w-24 h-24 rounded-full border-8 transition-all ${
                  status === 'green'
                    ? 'bg-green-500 border-green-600 shadow-2xl shadow-green-500/50 dark:bg-green-400 dark:border-green-500 animate-pulse'
                    : 'bg-green-200 border-green-300 opacity-20 dark:bg-green-900/30 dark:border-green-800'
                }`}
              />
            </div>
          </div>

          {/* Status Label */}
          <div className="mt-6 text-center">
            <p className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {statusLabel}
            </p>
            {stockStatusLight !== null && stockStatusLight.updated !== null && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Letzte Aktualisierung: {formatDate(stockStatusLight.updated)}
              </p>
            )}
          </div>
        </div>

        {/* Description & Details */}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Einschätzung
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              {statusDescription}
            </p>
          </div>

          {adminComment !== '' && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                Aktuelle Analyse
              </h4>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {adminComment}
              </p>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Ampel-Phasen
            </h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-red-500 rounded-full flex-shrink-0 mt-1"></div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Rot - Vorsicht</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Defensive Positionierung empfohlen. Stopps eng setzen. Neue Käufe nur selektiv.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-yellow-400 rounded-full flex-shrink-0 mt-1"></div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Gelb - Neutral</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Abwartende Haltung. Geduld zahlt sich aus. Auf klare Signale warten.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-green-500 rounded-full flex-shrink-0 mt-1"></div>
                <div>
                  <p className="font-semibold text-gray-900 dark:text-white">Grün - Optimistisch</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Günstige Marktphase. Positionsaufbau möglich. Risikomanagement beachten.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>Hinweis:</strong> Die Börsen-Ampel dient als Orientierungshilfe und basiert auf einer Gesamtbetrachtung verschiedener Marktfaktoren. Sie ersetzt keine individuelle Analyse und sollte im Kontext der eigenen Strategie betrachtet werden.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
